import type Konva from 'konva';
import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Layer, Stage } from 'react-konva';

import { BackgroundNode } from '../components/Stage/BackgroundNode';
import { SceneNode } from '../components/Stage/SceneNode';
import { AdScene } from '../model/types';
import { downloadStage, ExportFormat } from '../utils/export';
import { loadImage } from '../utils/image';

interface CaptureApi {
  /** Renderuje scenę poza ekranem i zapisuje plik PNG. */
  download: (scene: AdScene, fileName: string) => Promise<void>;
  /** Renderuje scenę i oddaje miniaturę JPEG o zadanym dłuższym boku. */
  capture: (scene: AdScene, maxSize: number) => Promise<Blob | null>;
  /** Ukryta plansza do wstawienia w drzewo. Bez niej nie ma czego renderować. */
  stage: ReactNode;
}

const NOOP = (): void => undefined;

/** Adresy obrazków, które muszą być w pamięci, zanim Konva narysuje planszę. */
const collectImageUrls = (scene: AdScene): string[] => {
  const urls = scene.elements
    .filter((element) => element.type === 'image')
    .map((element) => (element as { src: string }).src);
  if (scene.background.kind === 'image' && scene.background.imageSrc) {
    urls.push(scene.background.imageSrc);
  }
  return urls.filter(Boolean);
};

const nextFrame = (callback: () => void): number => requestAnimationFrame(() => requestAnimationFrame(callback));

/**
 * Renderowanie sceny poza ekranem.
 *
 * Plik musi powstać z tego samego kodu, który rysuje edytor, więc montujemy
 * planszę Konvy w ukrytym kontenerze zamiast pisać drugi renderer. Kolejność ma
 * znaczenie: najpierw dociągamy zdjęcia i kroje, dopiero potem montujemy scenę
 * i czekamy dwie klatki — Konva mierzy tekst i rysuje obrazki przy pierwszym
 * rysowaniu, więc bez tego wychodzą puste ramki i krój zastępczy.
 */
export const useSceneCapture = (): CaptureApi => {
  const stageRef = useRef<Konva.Stage>(null);
  const readyRef = useRef<(() => void) | null>(null);
  const [pending, setPending] = useState<AdScene | null>(null);

  /**
   * Sygnał „plansza gotowa" wysyłamy z efektu, czyli po zatwierdzeniu renderu
   * przez Reacta. Samo `setPending` niczego nie gwarantuje: zaplanowany render
   * może wypaść PO najbliższej klatce, a wtedy referencja do planszy jest pusta
   * i eksport po cichu nie robi nic.
   */
  useEffect(() => {
    if (!pending) return undefined;
    const frame = nextFrame(() => {
      readyRef.current?.();
      readyRef.current = null;
    });
    return () => cancelAnimationFrame(frame);
  }, [pending]);

  const mount = useCallback(async (scene: AdScene): Promise<Konva.Stage> => {
    await Promise.all(collectImageUrls(scene).map((url) => loadImage(url).catch(() => null)));
    if (document.fonts?.ready) await document.fonts.ready;

    // Limit czasu zamiast czekania w nieskończoność: gdyby sygnał gotowości
    // nie doszedł (na przykład po zmianie w cyklu życia komponentu), użytkownik
    // ma zobaczyć komunikat, a nie przycisk, który kręci się bez końca.
    await new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(
        () => reject(new Error('Przygotowanie planszy do eksportu trwało zbyt długo')),
        8000,
      );
      readyRef.current = () => {
        clearTimeout(timeout);
        resolve();
      };
      setPending(scene);
    });

    const stage = stageRef.current;
    if (!stage) throw new Error('Nie udało się przygotować planszy do eksportu');
    return stage;
  }, []);

  const download = useCallback(async (scene: AdScene, fileName: string): Promise<void> => {
    let stage: Konva.Stage;
    try {
      stage = await mount(scene);
    } catch (error) {
      setPending(null);
      throw error;
    }

    try {
      await downloadStage(stage, { scale: 1, zoom: 1, format: 'png' as ExportFormat, fileName });
    } finally {
      setPending(null);
    }
  }, [mount]);

  const capture = useCallback(async (scene: AdScene, maxSize: number): Promise<Blob | null> => {
    const stage = await mount(scene);
    try {
      const longEdge = Math.max(scene.width, scene.height) || 1;
      const blob = await stage.toBlob({
        mimeType: 'image/jpeg',
        quality: 0.8,
        pixelRatio: maxSize / longEdge,
      });
      return blob instanceof Blob ? blob : null;
    } finally {
      setPending(null);
    }
  }, [mount]);

  const stage = pending ? (
    <div style={{ position: 'fixed', left: -99999, top: 0, pointerEvents: 'none' }} aria-hidden>
      <Stage ref={stageRef} width={pending.width} height={pending.height}>
        <Layer>
          <BackgroundNode background={pending.background} width={pending.width} height={pending.height} />
          {pending.elements.map((element) => (
            <SceneNode
              key={element.id}
              element={element}
              isEditing={false}
              onSelect={NOOP}
              onDragStart={NOOP}
              onDragMove={NOOP}
              onDragEnd={NOOP}
              onTransformStart={NOOP}
              onTransformEnd={NOOP}
              onEditText={NOOP}
              onMeasure={NOOP}
            />
          ))}
        </Layer>
      </Stage>
    </div>
  ) : null;

  return { download, capture, stage };
};
